import React from 'react';
import { Plus, Pencil, Star, Printer, Trash2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';

/**
 * Les tables du Jour J et leurs QR codes (maquette Suivi Privatisation, écran 18e).
 *
 * Une table n'était qu'un numéro dans une liste de pastilles, ajouté un par un. La maquette lui donne
 * une fiche — un nombre de places, un libellé imprimé sur le QR, un marqueur « à réimprimer » — et
 * surtout la création PAR SÉRIE : « de la table 200 à la table 215 » pose seize QR d'un coup, ce qui
 * est exactement la façon dont on décrit un plan de salle avec un organisateur.
 *
 * Les règles portées par la maquette, et qui expliquent le reste :
 *  — LE NUMÉRO EST CELUI DE L'ÉTABLISSEMENT : 1 à 26, 201 à 226, ou choisis un par un. L'application
 *    ne renumérote rien.
 *  — UN QR PAR TABLE, jamais par convive : le même code sert à tous ceux qui s'y installent.
 *  — LES PLACES SONT FACULTATIVES : le QR fonctionne sans. Elles servent seulement à savoir quand une
 *    table est complète au passe.
 *  — UNE TABLE AJOUTÉE APRÈS L'IMPRESSION se signale, pour qu'on n'oublie pas d'imprimer son QR.
 */

export interface TableJourJ {
    numero: string;
    places: number | null;
    libelle: string;
    a_reimprimer: boolean;
    vip: boolean;
}

export const tableVide = (numero: string): TableJourJ => ({ numero, places: null, libelle: '', a_reimprimer: false, vip: false });

/** Trie comme on lit un plan de salle : 2 avant 10, et « VIP » après les nombres. */
export const trierTables = (tables: TableJourJ[]): TableJourJ[] => [...tables].sort((a, b) => {
    const na = Number(a.numero);
    const nb = Number(b.numero);
    const aNum = Number.isFinite(na);
    const bNum = Number.isFinite(nb);
    if (aNum && bNum) return na - nb;
    if (aNum) return -1;
    if (bNum) return 1;
    return a.numero.localeCompare(b.numero, 'fr');
});

export function TablesEditor({ tables, onChange, onImprimer, impressionEnCours }: {
    tables: TableJourJ[];
    onChange: (tables: TableJourJ[]) => void;
    onImprimer: () => void;
    impressionEnCours: boolean;
}) {
    const [ajoutOuvert, setAjoutOuvert] = React.useState(false);
    const [enEdition, setEnEdition] = React.useState<TableJourJ | null>(null);

    const totalPlaces = tables.reduce((t, x) => t + (x.places || 0), 0);
    const aReimprimer = tables.filter((t) => t.a_reimprimer).length;

    const ajouter = (nouvelles: TableJourJ[]) => {
        // « Les numéros déjà pris sont ignorés, pas dupliqués » (règle de la maquette).
        const connus = new Set(tables.map((t) => t.numero));
        const retenues = nouvelles.filter((t) => t.numero && !connus.has(t.numero));
        onChange(trierTables([...tables, ...retenues]));
        setAjoutOuvert(false);
    };

    const remplacer = (avant: string, apres: TableJourJ) => {
        onChange(trierTables(tables.map((t) => (t.numero === avant ? apres : t))));
        setEnEdition(null);
    };

    const retirer = (numero: string) => {
        onChange(tables.filter((t) => t.numero !== numero));
        setEnEdition(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="text-[13px] font-bold text-content">
                        {tables.length} table{tables.length > 1 ? 's' : ''}
                        {totalPlaces > 0 && <span className="font-normal text-content-muted"> pour {totalPlaces} personne{totalPlaces > 1 ? 's' : ''}</span>}
                    </p>
                    <p className="text-[11px] text-content-muted">
                        {tables.length} QR code{tables.length > 1 ? 's' : ''}
                        {aReimprimer > 0 && ` · ${aReimprimer} à réimprimer`}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => setAjoutOuvert(true)}>
                        <Plus size={14} /> Ajouter des tables
                    </Button>
                    <Button size="sm" onClick={onImprimer} disabled={tables.length === 0 || impressionEnCours}>
                        <Printer size={14} /> {impressionEnCours ? 'Génération…' : (tables.length > 1 ? `Imprimer les ${tables.length} QR` : 'Imprimer le QR')}
                    </Button>
                </div>
            </div>

            {tables.length === 0 ? (
                <p className="text-[12px] text-content-muted italic py-6 text-center border border-dashed border-border rounded-xl">
                    Aucune table. Créez-les une par une, ou toute une série d'un coup.
                </p>
            ) : (
                <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
                    {tables.map((t) => (
                        <button
                            key={t.numero}
                            type="button"
                            onClick={() => setEnEdition(t)}
                            className="w-full flex items-center gap-3 px-3.5 py-2.5 bg-surface text-left hover:bg-surface-soft transition-colors"
                        >
                            <span className="min-w-[54px] text-[13px] font-bold text-content tabular-nums shrink-0">{t.numero}</span>
                            <span className="text-[12px] text-content-muted min-w-0 flex-1 truncate">
                                {t.libelle || <span className="italic">Sans libellé</span>}
                            </span>
                            <span className="text-[12px] text-content-muted tabular-nums whitespace-nowrap shrink-0">
                                {t.places ? `${t.places} places` : '—'}
                            </span>
                            {t.vip && (
                                <span className="inline-flex items-center gap-1 h-[21px] px-2 rounded-full bg-state-warn-soft text-state-warn text-[10px] font-bold shrink-0">
                                    <Star size={10} /> VIP
                                </span>
                            )}
                            {t.a_reimprimer && (
                                <span className="inline-flex items-center gap-1 h-[21px] px-2 rounded-full bg-state-info-soft text-state-info text-[10px] font-bold whitespace-nowrap shrink-0">
                                    <Printer size={10} /> À réimprimer
                                </span>
                            )}
                            <Pencil size={14} className="text-content-muted shrink-0" />
                        </button>
                    ))}
                </div>
            )}

            {ajoutOuvert && <ModaleAjout onFermer={() => setAjoutOuvert(false)} onAjouter={ajouter} dejaPrises={tables.map((t) => t.numero)} />}
            {enEdition && <ModaleEdition table={enEdition} onFermer={() => setEnEdition(null)} onValider={remplacer} onRetirer={retirer} />}
        </div>
    );
}

/** « Une seule, ou toute une série d'un coup. » */
function ModaleAjout({ onFermer, onAjouter, dejaPrises }: {
    onFermer: () => void;
    onAjouter: (tables: TableJourJ[]) => void;
    dejaPrises: string[];
}) {
    const [mode, setMode] = React.useState<'une' | 'serie'>('serie');
    const [numero, setNumero] = React.useState('');
    const [debut, setDebut] = React.useState('');
    const [fin, setFin] = React.useState('');
    const [places, setPlaces] = React.useState('');

    const placesNum = Number(places) > 0 ? Math.min(99, Math.round(Number(places))) : null;
    const serie = React.useMemo(() => {
        const a = Number(debut);
        const b = Number(fin);
        if (!Number.isFinite(a) || !Number.isFinite(b) || a > b) return [] as string[];
        // Plafonné : une série de mille tables est une faute de frappe, pas un plan de salle.
        return Array.from({ length: Math.min(200, b - a + 1) }, (_, i) => String(a + i));
    }, [debut, fin]);
    const nouvelles = mode === 'une'
        ? (numero.trim() ? [numero.trim()] : [])
        : serie;
    const retenues = nouvelles.filter((n) => !dejaPrises.includes(n));
    const ignorees = nouvelles.length - retenues.length;

    return (
        <Modal
            open
            onClose={onFermer}
            size="sm"
            title="Ajouter des tables"
            subtitle="Une seule, ou toute une série d'un coup."
            footer={
                <>
                    <Button variant="outline" onClick={onFermer}>Annuler</Button>
                    <Button
                        onClick={() => onAjouter(retenues.map((n) => ({ ...tableVide(n), places: placesNum, a_reimprimer: true })))}
                        disabled={retenues.length === 0}
                    >
                        {retenues.length > 1 ? `Créer ${retenues.length} tables` : 'Créer la table'}
                    </Button>
                </>
            }
        >
            <div className="space-y-3">
                <div className="flex gap-1 p-1 rounded-[11px] bg-surface-soft border border-border">
                    {([['serie', 'Une série'], ['une', 'Une seule table']] as const).map(([cle, label]) => (
                        <button key={cle} type="button" onClick={() => setMode(cle)}
                            className={`flex-1 h-8 rounded-[8px] text-[12px] font-semibold transition-colors ${mode === cle ? 'bg-surface text-content shadow-sm' : 'text-content-muted'}`}>
                            {label}
                        </button>
                    ))}
                </div>

                {mode === 'une' ? (
                    <label className="block">
                        <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">Numéro affiché</span>
                        <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="206, ou « Table VIP »"
                            className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                    </label>
                ) : (
                    <div className="flex items-end gap-2">
                        <label className="flex-1 min-w-0">
                            <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">De la table</span>
                            <input value={debut} onChange={(e) => setDebut(e.target.value)} inputMode="numeric" placeholder="200"
                                className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                        </label>
                        <label className="flex-1 min-w-0">
                            <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">à la table</span>
                            <input value={fin} onChange={(e) => setFin(e.target.value)} inputMode="numeric" placeholder="215"
                                className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                        </label>
                    </div>
                )}

                <label className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">Places par table <span className="font-normal normal-case tracking-normal">(facultatif)</span></span>
                    <input value={places} onChange={(e) => setPlaces(e.target.value)} inputMode="numeric" placeholder="8"
                        className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                </label>

                <p className="text-[11.5px] leading-relaxed text-content-muted">
                    Le QR fonctionne sans les places : elles servent seulement à savoir quand une table est
                    complète au passe. Les tables créées ici sont marquées « à réimprimer ».
                    {ignorees > 0 && <> {ignorees} numéro{ignorees > 1 ? 's' : ''} déjà pris {ignorees > 1 ? 'seront ignorés' : 'sera ignoré'}.</>}
                </p>
            </div>
        </Modal>
    );
}

/** La fiche d'une table : ce qu'on imprime, combien de places, et le retrait. */
function ModaleEdition({ table, onFermer, onValider, onRetirer }: {
    table: TableJourJ;
    onFermer: () => void;
    onValider: (avant: string, apres: TableJourJ) => void;
    onRetirer: (numero: string) => void;
}) {
    const [numero, setNumero] = React.useState(table.numero);
    const [places, setPlaces] = React.useState(table.places ? String(table.places) : '');
    const [libelle, setLibelle] = React.useState(table.libelle);
    const [vip, setVip] = React.useState(table.vip);

    return (
        <Modal
            open
            onClose={onFermer}
            size="sm"
            title={`Table ${table.numero}`}
            subtitle="Décisions sur les tables"
            footerLayout="split"
            footer={
                <>
                    <button type="button" onClick={() => onRetirer(table.numero)}
                        className="h-9 inline-flex items-center gap-2 px-3.5 rounded-full text-[13px] font-semibold text-[#8f5450] hover:bg-state-danger-soft transition-colors">
                        <Trash2 size={14} /> Retirer la table
                    </button>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={onFermer}>Annuler</Button>
                        <Button
                            onClick={() => onValider(table.numero, {
                                numero: numero.trim() || table.numero,
                                places: Number(places) > 0 ? Math.min(99, Math.round(Number(places))) : null,
                                libelle: libelle.trim().slice(0, 60),
                                // Changer le numéro ou le libellé change le QR imprimé : il faut le refaire.
                                a_reimprimer: table.a_reimprimer || numero.trim() !== table.numero || libelle.trim() !== table.libelle,
                                vip,
                            })}
                        >
                            Enregistrer
                        </Button>
                    </div>
                </>
            }
        >
            <div className="space-y-3">
                <label className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">Numéro affiché</span>
                    <input value={numero} onChange={(e) => setNumero(e.target.value)}
                        className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                </label>
                <label className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">Places <span className="font-normal normal-case tracking-normal">(facultatif)</span></span>
                    <input value={places} onChange={(e) => setPlaces(e.target.value)} inputMode="numeric" placeholder="8"
                        className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                </label>
                <label className="block">
                    <span className="block text-[11px] font-bold uppercase tracking-[.08em] text-content-muted mb-1.5">Libellé sur le QR imprimé</span>
                    <input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Table ronde, côté jardin…"
                        className="w-full h-9 bg-surface border border-border rounded-[9px] px-3 text-[13px] text-content outline-none focus:border-content" />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer pt-1">
                    <span className="text-[12.5px] text-content">Table VIP <span className="text-content-muted">— toujours en tête de liste au passe</span></span>
                    <input type="checkbox" checked={vip} onChange={(e) => setVip(e.target.checked)} className="accent-brand-600 w-4 h-4 shrink-0" />
                </label>
                <p className="text-[11.5px] leading-relaxed text-content-muted">
                    Retirer une table libère ses convives : ceux qui s'y étaient installés repassent en
                    « non installé » et pourront scanner une autre table.
                </p>
            </div>
        </Modal>
    );
}
